import { Check, Minus, MoveRight } from '@/ui/icons'
import { Link } from "react-router-dom";
import { Badge } from "@/ui/components";
import { Button } from "@/ui/components";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

function Pricing() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <div ref={sectionRef} className="w-full py-20 lg:py-40 bg-earth-50">
      <div className="container mx-auto px-6">
        <motion.div 
          className="flex text-center justify-center items-center gap-4 flex-col"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }}
        >
          <Badge>Pricing</Badge>
          <div className="flex gap-2 flex-col">
            <h2 className="text-display-lg md:text-display-xl max-w-xl text-center">
              Choose the plan that fits your adventures
            </h2>
            <p className="text-body-md md:text-body-lg text-muted-foreground max-w-xl text-center">
              Start free, upgrade to Pro when you are planning seriously. Businesses can reach out for custom partnerships.
            </p>
          </div>
        </motion.div>
        {/* Desktop: Comparison Table */}
          <div className="hidden lg:grid text-left w-full grid-cols-3 divide-x pt-20 max-w-4xl mx-auto">
            {/* Header row - empty first column */}
            <div className="col-span-1"></div>
            
            {/* Free Tier */}
            <div className="px-3 py-1 md:px-6 md:py-4 gap-2 flex flex-col">
              <p className="text-2xl font-semibold">Free</p>
              <p className="text-sm text-muted-foreground">
                Perfect for solo explorers testing the platform and planning casual trips.
              </p>
              <p className="flex flex-col lg:flex-row lg:items-center gap-2 text-xl mt-8">
                <span className="text-4xl font-bold">$0</span>
                <span className="text-sm text-muted-foreground"> / month</span>
              </p>
              <Link to="/signup">
                <Button variant="outline" className="gap-4 mt-8 w-full">
                  Get Started <MoveRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            {/* Pro Tier */}
            <div className="px-3 py-1 md:px-6 md:py-4 gap-2 flex flex-col border-2 border-adventure-600 rounded-lg -mx-0.5 relative">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-adventure-600 text-white">
                Most Popular
              </Badge>
              <p className="text-2xl font-semibold">Pro</p>
              <p className="text-sm text-muted-foreground">
                For serious overlanders and regular trip organizers who need unlimited planning capabilities.
              </p>
              <p className="flex flex-col lg:flex-row lg:items-baseline gap-2 text-xl mt-8">
                <span className="text-4xl font-bold">$39.99</span>
                <span className="text-sm text-muted-foreground"> / year</span>
              </p>
              <p className="text-sm text-muted-foreground">or $4.99 / month</p>
              <Link to="/signup">
                <Button className="gap-4 mt-8 bg-adventure-600 hover:bg-adventure-700 w-full">
                  Start Free Trial <MoveRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            {/* Features header */}
            <div className="px-6 col-span-1 py-4 font-semibold">
              Features
            </div>
            <div></div>
            <div></div>
            
            {/* Active Trips */}
            <div className="px-6 col-span-1 py-4">Active Trips</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
              <span className="text-xs ml-1">Unlimited</span>
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
              <span className="text-xs ml-1">Unlimited</span>
            </div>
            
            {/* Crew Members Per Trip */}
            <div className="px-6 col-span-1 py-4">Crew Members Per Trip</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <p className="text-sm text-muted-foreground">8 members</p>
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
              <span className="text-xs ml-1">Unlimited</span>
            </div>
            
            {/* Photo Storage */}
            <div className="px-6 col-span-1 py-4">Photo Storage</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <p className="text-sm text-muted-foreground">1 GB</p>
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <p className="text-sm text-muted-foreground">5 GB</p>
            </div>
            
            {/* Vehicles */}
            <div className="px-6 col-span-1 py-4">Vehicles</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
              <span className="text-xs ml-1">Unlimited</span>
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
              <span className="text-xs ml-1">Unlimited</span>
            </div>
            
            {/* GPX Import */}
            <div className="px-6 col-span-1 py-4">GPX Import</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>

            {/* Ranger AI Assistant */}
            <div className="px-6 col-span-1 py-4">Ranger AI Assistant</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Minus className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>

            {/* Dispersed Campsite Finder */}
            <div className="px-6 col-span-1 py-4">Dispersed Campsite Finder</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Minus className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>

            {/* Campspotter Weather */}
            <div className="px-6 col-span-1 py-4">Campspotter Weather</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Minus className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>

            {/* Per-Link Sharing Permissions */}
            <div className="px-6 col-span-1 py-4">Per-Link Sharing Permissions</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Minus className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>

            {/* 3D Route Tours */}
            <div className="px-6 col-span-1 py-4">3D Route Tours</div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <p className="text-sm text-muted-foreground">Preview only</p>
            </div>
            <div className="px-3 py-1 md:px-6 md:py-4 flex justify-center">
              <Check className="w-4 h-4 text-adventure-600" />
            </div>
          </div>
          
          {/* Mobile: Stacked Cards */}
          <div className="lg:hidden space-y-8 pt-20 max-w-4xl mx-auto">
            {/* Free Tier Card */}
            <div className="border rounded-lg p-6">
              <div className="mb-4">
                <p className="text-2xl font-semibold">Free</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Perfect for solo explorers testing the platform and planning casual trips.
                </p>
                <p className="flex items-center gap-2 text-xl mt-4">
                  <span className="text-4xl font-bold">$0</span>
                  <span className="text-sm text-muted-foreground"> / month</span>
                </p>
              </div>
              <Link to="/signup">
                <Button variant="outline" className="gap-4 w-full">
                  Get Started <MoveRight className="w-4 h-4" />
                </Button>
              </Link>
              <div className="mt-6 space-y-3 pt-6 border-t">
                <div className="flex justify-between items-center">
                  <span>Active Trips</span>
                  <span className="text-sm text-muted-foreground">Unlimited</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Photo Storage</span>
                  <span className="text-sm text-muted-foreground">1 GB</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Crew Members</span>
                  <span className="text-sm text-muted-foreground">8 per trip</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Vehicles</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>GPX Import</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>3D Route Tours</span>
                  <span className="text-sm text-muted-foreground">Preview only</span>
                </div>
              </div>
            </div>

            {/* Pro Tier Card */}
            <div className="border-2 border-adventure-600 rounded-lg p-6 relative">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-adventure-600 text-white">
                Most Popular
              </Badge>
              <div className="mb-4">
                <p className="text-2xl font-semibold">Pro</p>
                <p className="text-sm text-muted-foreground mt-2">
                  For serious overlanders and regular trip organizers who need unlimited planning capabilities.
                </p>
                <p className="flex items-baseline gap-2 text-xl mt-4">
                  <span className="text-4xl font-bold">$39.99</span>
                  <span className="text-sm text-muted-foreground"> / year</span>
                </p>
                <p className="text-sm text-muted-foreground mt-1">or $4.99 / month</p>
              </div>
              <Link to="/signup">
                <Button className="gap-4 w-full bg-adventure-600 hover:bg-adventure-700">
                  Start Free Trial <MoveRight className="w-4 h-4" />
                </Button>
              </Link>
              <div className="mt-6 space-y-3 pt-6 border-t">
                <div className="flex justify-between items-center">
                  <span>Active Trips</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>Crew Members</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>Photo Storage</span>
                  <span className="text-sm text-muted-foreground">5 GB</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Vehicles</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>GPX Import</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>Ranger AI Assistant</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>Dispersed Campsite Finder</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>Campspotter Weather</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>Per-Link Sharing Permissions</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
                <div className="flex justify-between items-center">
                  <span>3D Route Tours</span>
                  <Check className="w-4 h-4 text-adventure-600" />
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}

export { Pricing };
